import { Article } from '@wix/answers-api';
import { Box, Button, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';

import { ArticleSummary } from './ArticleSummary';
import { CopilotHeader } from './CopilotHeader';
import { CopilotLoader } from './CopilotLoader';
import { CopilotMessageInput } from './CopilotMessageInput';
import { isCopilotChatbotVisible } from './iframeMessageHandler';
import { RelatedArticlesMini } from './RelatedArticlesMini';
import { UnhelpfulFeedbackChat } from './UnhelpfulFeedbackChat';
import { useChatbotMessageSender } from './useChatbotMessageSender';
import { DATA_HOOKS } from '../../dataHooks';
import { PanelAction, PanelType } from '../../types';
import { useArticlePageBi } from '../../utils/article-page-bi';
import { useStore } from '../../zustand/stores';
import { ChatbotFlow, CopilotDisplayMode } from '../../zustand/stores/copilot';
const ChatbotIframe = dynamic(() => import('./ChatbotIframe'), { ssr: false });

import css from './index.module.scss';


type ArticleRightSideBarProps = {
  showRightSideBar: boolean;
  setShowRightSideBar: (showRightSideBar: boolean) => void;
  article: Article;
  selfArticleRedirect: (url: string, shallow?: boolean, targetLocale?: string) => void;
  summaryRef: React.RefObject<HTMLDivElement>;
  relatedArticles: Article[];
};

export const ArticleRightSideBar =
  ({ showRightSideBar, setShowRightSideBar, article, selfArticleRedirect, summaryRef, relatedArticles }:
    ArticleRightSideBarProps) => {
    const {
      copilotDisplayMode,
      setCopilotDisplayMode,
      setIsChatbotInitializing,
      isChatbotReady,
      chatbotFlow,
      setUnreadMessagesCount
    } = useStore(state => state.copilotSlice);
    const { chatNotificationTrack, assistantPanelVisibleTrack } = useArticlePageBi(article);
    const [hasTrackedVisibility, setHasTrackedVisibility] = useState(false);
    const [prevShowState, setPrevShowState] = useState(showRightSideBar);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const { summary, relatedQuestions } = article;
    const { t } = useTranslation();
    const { panelTrack } = useArticlePageBi(article);
    const { sendMessage } = useChatbotMessageSender({});

    const goToChatbot = async () => {
      await chatNotificationTrack();
      if (isChatbotReady) {
        setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
      } else {
        setIsChatbotInitializing(true);
      }
    };

    const onCloseClick = async () => {
      await panelTrack(PanelType.ASSISTANT, PanelAction.COLLAPSE);
      setShowRightSideBar(false);
    };


    useEffect(() => {
      if (prevShowState !== showRightSideBar) {
        setShouldAnimate(true);
        setPrevShowState(showRightSideBar);
      }
    }, [showRightSideBar, prevShowState]);

    useEffect(() => {
      if (copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback) {
        setShowRightSideBar(true);
      }
    }, [copilotDisplayMode, setShowRightSideBar]);

    useEffect(() => {
      if (showRightSideBar && !hasTrackedVisibility) {
        setHasTrackedVisibility(true);
        void assistantPanelVisibleTrack();
      }
    }, [showRightSideBar, assistantPanelVisibleTrack, hasTrackedVisibility]);

    const goToChat = () => {
      if (isChatbotReady) {
        setCopilotDisplayMode(CopilotDisplayMode.Chatbot);
      } else {
        setIsChatbotInitializing(true);
      }
    };

    useEffect(() => {
      isCopilotChatbotVisible(showRightSideBar && copilotDisplayMode === CopilotDisplayMode.Chatbot);
      if (isChatbotReady && showRightSideBar && copilotDisplayMode === CopilotDisplayMode.Chatbot) {
        setUnreadMessagesCount(0);
      }
    }, [showRightSideBar, copilotDisplayMode, isChatbotReady, setUnreadMessagesCount]);

    return (<Box
      className={
        classNames(css.wrapper,
          {
            [css.showRightSideBar]: shouldAnimate && showRightSideBar,
            [css.hideRightSideBar]: shouldAnimate && !showRightSideBar,
            [css.staticHidden]: !shouldAnimate && !showRightSideBar,
            [css.showChatbot]: copilotDisplayMode === CopilotDisplayMode.Chatbot
          }
        )
      }
      direction="vertical"
    >
      <ChatbotIframe
        setShowRightSideBar={setShowRightSideBar}
        selfArticleRedirect={selfArticleRedirect}
      />
      {
        copilotDisplayMode === CopilotDisplayMode.UnhelpfulFeedback && (
          <UnhelpfulFeedbackChat article={article} sendMessage={sendMessage} />
        )
      }
      {<Box direction='vertical' className={classNames(css.mainContent,
        {
          [css.mainContentVisible]:
            copilotDisplayMode === CopilotDisplayMode.CopilotMainView ||
            copilotDisplayMode === CopilotDisplayMode.Loading,
          [css.mainContentHidden]:
            copilotDisplayMode !== CopilotDisplayMode.CopilotMainView &&
            copilotDisplayMode !== CopilotDisplayMode.Loading
        }
      )}>
        <Box direction='vertical' className={css.topSection}>
          <CopilotHeader
            onChatClick={goToChatbot}
            onCloseClick={onCloseClick}
            dismissDataHook={DATA_HOOKS.ARTICLE_RIGHT_SIDEBAR_DISMISS_BUTTON}
          />
          {summary && copilotDisplayMode !== CopilotDisplayMode.Loading &&
            <ArticleSummary summary={summary} article={article} summaryRef={summaryRef} />}
          <RelatedArticlesMini
            relatedArticles={relatedArticles}
            article={article}
            selfArticleRedirect={selfArticleRedirect} />
          {copilotDisplayMode === CopilotDisplayMode.Loading && <CopilotLoader />}
        </Box>
        {copilotDisplayMode !== CopilotDisplayMode.Loading && chatbotFlow === ChatbotFlow.REGULAR &&
          <CopilotMessageInput relatedQuestions={relatedQuestions} article={article} sendMessage={sendMessage} />}
      </Box>
      }
      {chatbotFlow === ChatbotFlow.CONTACT &&
        [CopilotDisplayMode.CopilotMainView, CopilotDisplayMode.UnhelpfulFeedback].includes(copilotDisplayMode) &&
        <Box className={css.ongoingLiveChat} gap="SP3">
          <Text size="small" weight="thin">{t('article-page.copilot.live-chat.ongoing.conversation.txt')}</Text>
          <Button className={css.goToChatButton} size="small" skin='dark' onClick={goToChat}>{t('article-page.copilot.go.to.chat.btn')}</Button>
        </Box>
      }
    </Box>
    );
  };
